/**
 * Small overlays for the contact board: project picker (global view), footer drop zones and the
 * morphing drag preview rendered by the App-level DragOverlay.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { FolderKanban, Layers, X, ArrowRight, CheckCircle2, Trash2 } from 'lucide-react';
import { DeleteDropZone } from '../../DeleteDropZone';
import { PriorityDropZones } from '../../PriorityDropZones';
import { ProjectPickRequest } from '../useContactBoard';
import { useCommDragMeta } from '../commDragStore';
import { PersonAvatar } from './shared';

export const ProjectPickerDialog: React.FC<{ request: ProjectPickRequest | null; onResolve: (projectId: string | null) => void }> = ({ request, onResolve }) => {
  if (!request) return null;
  return createPortal(
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-150" onClick={() => onResolve(null)}>
      <div className="w-[360px] max-w-[92vw] rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-4 animate-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 mb-3">
          {request.person ? (
            <PersonAvatar name={request.person.name} organization={request.person.organization} size={32} />
          ) : (
            <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-950/60 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
              <FolderKanban size={16} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate">
              {request.person ? `Neuer Vorgang mit ${request.person.name}` : 'Neuer Vorgang'}
            </div>
            <div className="text-[11px] text-slate-500">In welchem Projekt?</div>
          </div>
          <button type="button" onClick={() => onResolve(null)} className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X size={16} /></button>
        </div>
        <div className="space-y-1">
          {request.options.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => onResolve(o.id)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left text-[12.5px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-950/40 transition-colors"
            >
              <FolderKanban size={14} className="text-slate-400" />
              <span className="flex-1 truncate">{o.name}</span>
              <ArrowRight size={13} className="text-slate-300" />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

export const DoneDropZone: React.FC<{
  activeDragId: string | number | null;
  droppedZoneId?: string | null;
}> = ({ activeDragId, droppedZoneId }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'status-completed',
  });

  const isActiveDrag = activeDragId !== null;
  const isDropped = droppedZoneId === 'status-completed';

  return (
    <div ref={setNodeRef} className="p-3 -m-3">
      <div
        className={`
          flex items-center gap-2 rounded-xl transition-all duration-300 ease-out select-none border
          ${isActiveDrag ? 'cursor-grabbing' : 'cursor-default'}
          ${isActiveDrag 
            ? 'px-6 py-3.5 border-dashed border-emerald-400/40 dark:border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-950/20 scale-105 shadow-sm' 
            : 'px-4 py-2 border-transparent bg-transparent hover:bg-emerald-50/30 dark:hover:bg-emerald-950/20 hover:border-emerald-300/40 dark:hover:border-emerald-800/40'}
          ${isOver 
            ? '!bg-emerald-100 dark:!bg-emerald-900/60 scale-120 !border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.4)] z-20' 
            : ''}
          ${isDropped 
            ? '!bg-emerald-600 !border-emerald-600 text-white scale-110 shadow-lg shadow-emerald-600/20 z-10 animate-pulse' 
            : ''}
        `}
      >
        <div className={`transition-colors duration-200 ${isDropped ? 'text-white' : 'text-emerald-500'}`}>
          <CheckCircle2 size={isActiveDrag ? 15 : 14} strokeWidth={2.5} />
        </div>
        <span className={`
          font-black uppercase tracking-wider transition-all duration-300
          ${isActiveDrag ? 'text-xs' : 'text-[10px]'}
          ${isDropped ? 'text-white' : isOver ? 'text-emerald-700 dark:text-emerald-200' : 'text-slate-400'}
        `}>
          {isDropped ? 'Erledigt!' : 'Erledigt'}
        </span>
      </div>
    </div>
  );
};

export const CommunicationFooterDropZones: React.FC<{
  activeDragId: string | null;
  droppedZoneId: string | null;
  isReadOnly?: boolean;
}> = ({ activeDragId, droppedZoneId, isReadOnly }) => {
  if (isReadOnly) return null;
  const isDragging = activeDragId !== null;
  return (
    <div className="shrink-0 border-t border-slate-200/40 dark:border-slate-800/40 bg-slate-50/40 dark:bg-slate-900/20 backdrop-blur-sm relative z-50">
      <div className={`px-6 flex items-center justify-center transition-all duration-300 ease-out ${isDragging ? 'py-6' : 'py-3'}`}>
        <div className={`flex items-center flex-nowrap bg-white/75 dark:bg-slate-900/75 backdrop-blur-md rounded-2xl border border-slate-200/50 dark:border-slate-800/50 shadow-xs transition-all duration-300 ease-out ${isDragging ? 'p-3.5 gap-6' : 'p-2 gap-3.5'}`}>
          <DeleteDropZone activeDragId={activeDragId} droppedZoneId={droppedZoneId} />
          <div className={`bg-slate-200/60 dark:bg-slate-800/80 shrink-0 ${isDragging ? 'w-px h-6 mx-3' : 'w-px h-4 mx-1.5'}`} />
          <DoneDropZone activeDragId={activeDragId} droppedZoneId={droppedZoneId} />
          <div className={`bg-slate-200/60 dark:bg-slate-800/80 shrink-0 ${isDragging ? 'w-px h-6 mx-3' : 'w-px h-4 mx-1.5'}`} />
          <PriorityDropZones activeDragId={activeDragId} droppedZoneId={droppedZoneId} />
        </div>
      </div>
    </div>
  );
};

/** Morphing preview: changes its wording depending on what is dragged and where. */
export const CommDragPreview: React.FC = () => {
  const meta = useCommDragMeta();
  if (!meta) return null;
  const over = meta.overColumn;
  const isDelete = meta.overZone === 'action-delete';

  let label = meta.title;
  let tone = 'bg-indigo-600/95 border-indigo-400/40';
  let Icon: React.ElementType | null = null;

  if (isDelete) {
    label = meta.kind === 'person' ? `${meta.personName || 'Person'} löschen` : meta.kind === 'stack' ? `${meta.count} Vorgänge löschen` : `„${meta.title}“ löschen`;
    tone = 'bg-rose-600/95 border-rose-400/40';
    Icon = Trash2;
  } else if (meta.kind === 'person') {
    if (over === 'open' || over === 'pending') { label = `Neuer Vorgang mit ${meta.personName || 'Person'}`; tone = over === 'open' ? 'bg-blue-600/95 border-blue-400/40' : 'bg-amber-500/95 border-amber-300/40'; }
    else if (over === 'completed') { label = `Gespräch mit ${meta.personName || 'Person'} protokollieren`; tone = 'bg-emerald-600/95 border-emerald-400/40'; Icon = CheckCircle2; }
  } else if (meta.kind === 'stack') {
    label = over === 'completed' ? `${meta.count} Vorgänge erledigen` : over === 'pool' ? `${meta.count} Vorgänge verwerfen` : `${meta.count} Vorgänge verschieben`;
    Icon = Layers;
    if (over === 'pool') tone = 'bg-rose-600/95 border-rose-400/40';
  } else if (meta.kind === 'vorgang') {
    if (over === 'pool') { label = `„${meta.title}“ verwerfen`; tone = 'bg-rose-600/95 border-rose-400/40'; }
    else if (over === 'completed' && meta.sourceColumn !== 'completed') { label = `„${meta.title}“ erledigen`; tone = 'bg-emerald-600/95 border-emerald-400/40'; Icon = CheckCircle2; }
  }

  return (
    <div
      style={{ transform: 'rotate(-1.5deg)', pointerEvents: 'none' }}
      className={`flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full border text-white font-semibold text-xs max-w-[280px] select-none shadow-xl shadow-indigo-900/20 transition-colors duration-150 ${tone}`}
    >
      {meta.personName ? (
        <PersonAvatar name={meta.personName} organization={meta.organization} size={22} ring />
      ) : <span className="w-1.5" />}
      {Icon && <Icon size={13} className="shrink-0 text-white/85" />}
      <span className="truncate">{label}</span>
    </div>
  );
};
